import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Info, Upload, Wand2 } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import { Agent, useAgentsStore } from "@/lib/agents-store";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const agentSchema = z.object({
  name: z.string().min(3, "Name required"),
  displayName: z.string().min(3, "Display name required"),
  role: z.string().min(1, "Role required"),
  description: z.string(),
  systemPrompt: z.string().min(10, "System prompt required"),
  model: z.string(),
  temperature: z.number().min(0).max(2),
  price: z.number().optional(),
});

type AgentFormValues = z.infer<typeof agentSchema>;

interface AgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editAgent?: Agent;
}

export function AgentModal({ open, onOpenChange, editAgent }: AgentModalProps) {
  const { t } = useTranslation();
  const addAgent = useAgentsStore((state) => state.addAgent);
  const updateAgent = useAgentsStore((state) => state.updateAgent);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema),
    defaultValues: {
      name: editAgent?.name || "",
      displayName: editAgent?.displayName || "",
      role: editAgent?.role || "Support",
      description: editAgent?.description || "",
      systemPrompt: editAgent?.systemPrompt || "",
      model: editAgent?.model || "GPT-4o",
      temperature: editAgent?.temperature || 0.7,
      price: editAgent?.price || 0,
    },
  });

  const handleSuggestConfig = () => {
    const currentName = form.getValues("displayName") || form.getValues("name") || "Agent";
    form.setValue(
      "systemPrompt",
      `Bạn là ${currentName}, một agent chuyên trách. Luôn trả lời ngắn gọn, chính xác, ưu tiên an toàn dữ liệu và nêu rõ giới hạn khi thiếu thông tin.`,
    );
    toast.success("Đã tạo gợi ý cấu hình cho agent.");
  };

  const onSubmit = async (data: AgentFormValues) => {
    setIsLoading(true);
    try {
      const nextAgent: Agent = {
        id: editAgent?.id || `agent-${crypto.randomUUID()}`,
        ...data,
        status: editAgent?.status || "draft",
        avatarUrl:
          editAgent?.avatarUrl || "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=256&h=256&fit=crop",
        languages: editAgent?.languages || ["vi", "en"],
        tools: editAgent?.tools || [],
        deployment: editAgent?.deployment || [],
        maxTokens: editAgent?.maxTokens || 4000,
        metrics: editAgent?.metrics || { runs: 0, avgCost: 0, successRate: 0 },
        price: data.price || 0,
      };

      if (editAgent) {
        updateAgent(editAgent.id, nextAgent);
      } else {
        addAgent(nextAgent);
      }

      toast.success(editAgent ? "Đã cập nhật agent." : "Đã tạo agent mới.");
      onOpenChange(false);
    } catch {
      toast.error("Không thể lưu agent.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden border-white/5 bg-card p-0 shadow-2xl">
        <DialogHeader className="border-b border-white/5 bg-muted/30 p-6">
          <div className="flex items-center justify-between">
            <DialogTitle>{editAgent ? `Chỉnh sửa ${editAgent.name}` : t("create_agent")}</DialogTitle>
            <Button variant="outline" size="sm" className="h-8 gap-2 text-xs" type="button" onClick={handleSuggestConfig}>
              <Wand2 className="h-3.5 w-3.5" /> Gợi ý cấu hình
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="h-12 w-full justify-start rounded-none border-b border-white/5 bg-transparent px-6">
              <TabsTrigger value="basic" className="h-full rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t("tab_basic")}
              </TabsTrigger>
              <TabsTrigger value="ai" className="h-full rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t("tab_ai")}
              </TabsTrigger>
              <TabsTrigger value="tools" className="h-full rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t("tab_tools")}
              </TabsTrigger>
              <TabsTrigger value="deploy" className="h-full rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t("tab_deploy")}
              </TabsTrigger>
            </TabsList>

            <div className="max-h-[60vh] overflow-y-auto p-6">
              <TabsContent value="basic" className="m-0 space-y-4">
                <div className="flex items-start gap-6">
                  <div
                    className="group flex h-24 w-24 shrink-0 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-secondary transition-colors hover:border-primary/50"
                    onClick={() => toast.info("Bạn có thể nối tính năng tải avatar riêng khi media backend đã sẵn sàng.")}
                  >
                    <Upload className="h-6 w-6 text-muted-foreground group-hover:text-primary" />
                    <span className="mt-2 text-[10px] text-muted-foreground">Avatar</span>
                  </div>

                  <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t("agent_name")}*</Label>
                        <Input placeholder="support-specialist" {...form.register("name")} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t("display_name")}*</Label>
                        <Input placeholder="Chuyên viên Hỗ trợ" {...form.register("displayName")} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t("role")}</Label>
                      <select className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" {...form.register("role")}>
                        <option value="Support">Support</option>
                        <option value="Sales">Sales</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Developer">Developer</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mô tả</Label>
                  <Textarea placeholder="Mô tả mục tiêu, phạm vi hỗ trợ và kiểu nhiệm vụ mà agent này phụ trách..." {...form.register("description")} />
                </div>
              </TabsContent>

              <TabsContent value="ai" className="m-0 space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("model")}</Label>
                    <select className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" {...form.register("model")}>
                      <option value="GPT-4o">GPT-4o (Khuyến nghị)</option>
                      <option value="GPT-4-Turbo">GPT-4 Turbo</option>
                      <option value="Claude-3.5-Sonnet">Claude 3.5 Sonnet</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>{t("temperature")}</Label>
                      <span className="font-mono text-xs">{form.watch("temperature")}</span>
                    </div>
                    <Slider defaultValue={[form.getValues().temperature]} max={2} step={0.1} onValueChange={(value) => form.setValue("temperature", value[0])} />
                  </div>

                  <div className="space-y-2">
                    <Label>{t("system_prompt")}*</Label>
                    <Textarea
                      className="min-h-[150px] font-mono text-xs leading-relaxed"
                      placeholder="Bạn là một agent AI chuyên trách. Hãy trả lời rõ ràng, an toàn và đúng phạm vi nhiệm vụ..."
                      {...form.register("systemPrompt")}
                    />
                    <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Info className="h-3 w-3" /> Mô tả rõ persona, giọng điệu, giới hạn và tiêu chí an toàn để hành vi agent ổn định hơn.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tools" className="m-0 space-y-4">
                <p className="text-sm text-muted-foreground">Chọn các tool mà agent được phép sử dụng khi thực thi tác vụ.</p>
                <div className="grid grid-cols-2 gap-4">
                  {["Web Search", "Gmail", "Calendar", "File Browser", "Calculator", "Code Interpreter"].map((tool) => (
                    <div key={tool} className="flex items-center space-x-3 rounded-lg border bg-muted/20 p-3">
                      <Checkbox id={tool} />
                      <Label htmlFor={tool} className="cursor-pointer text-sm font-medium leading-none">
                        {tool}
                      </Label>
                    </div>
                  ))}
                </div>
                <div className="pt-4">
                  <Button variant="outline" className="w-full border-dashed" type="button" onClick={() => toast.info("Khai báo tool OpenAPI tùy chỉnh sẽ được mở rộng ở bước tích hợp schema sâu hơn.")}>
                    + Thêm tool OpenAPI tùy chỉnh
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="deploy" className="m-0 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg border bg-secondary/20 p-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Hiển thị trên Marketplace</p>
                      <p className="text-xs text-muted-foreground">Cho phép agent xuất hiện trong marketplace nội bộ của tổ chức.</p>
                    </div>
                    <Switch />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("price")} ($)</Label>
                    <Input type="number" placeholder="0.00" {...form.register("price", { valueAsNumber: true })} />
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="border-t border-white/5 bg-muted/30 p-6">
            <Button variant="ghost" onClick={() => onOpenChange(false)} type="button">
              {t("cancel")}
            </Button>
            <Button className="min-w-[120px] bg-gradient-primary text-primary-foreground hover:opacity-95" disabled={isLoading}>
              {isLoading ? "Đang lưu..." : t("save_agent")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
